import { useCallback, useEffect, useRef, useState } from 'react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import appPageCopy from '@/i18n/ns/app-page';
import appPublishCopy from '@/i18n/ns/app-publish';
import { formatCount } from '@/i18n/format';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { SessionChangedError } from '@/lib/session-guard';
import { ErrorState, LoadingState } from '../../primitives';
import { findingsCopyFrom } from './findings-copy';
import { FindingsList } from './findings-list';
import { CheckReportUnavailable, type FindingAcknowledgment } from './check-report-api';
import type { CheckReport } from './findings-model';

export function checkedAt(iso: string | null, locale: string): string | null {
  if (!iso) return null;
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso),
    );
  } catch {
    return null;
  }
}

/**
 * The one selected version's check: `read` is the CURRENT report; `scan` (editors only) runs a real scan of
 * the saved source and answers the new report. Plain retries: no operation id.
 */
export type CheckReportReader = {
  read: () => Promise<CheckReport>;
  scan?: () => Promise<CheckReport>;
  /** Owners only, where the server has the audited acknowledgment. */
  acknowledge?: (input: FindingAcknowledgment, operationId: string) => Promise<CheckReport>;
};

/**
 * The selected version's Checks: the server's report shown as it is (secrets and held packages first, quiet
 * warnings after). No Fix (the repair primitive does not exist yet). Publish anyway is offered only when the
 * reader has `acknowledge` AND the fresh report says the owner can; its confirm sends one operation id per
 * (report, finding), kept across an unknown outcome and dropped once the server answers. A read that fails says so with a plain Try again; a replaced session shows
 * nothing at all (silent retirement).
 */
export function AppChecksMount({
  checks,
  reveal,
}: {
  checks: CheckReportReader;
  /** A review request: each new token brings this section into view and focus. */
  reveal?: unknown;
}) {
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (reveal === undefined) return;
    root.current?.scrollIntoView?.({ block: 'start' });
    root.current?.focus?.();
  }, [reveal]);
  return (
    <div ref={root} tabIndex={-1} data-testid="app-checks">
      <ChecksBody checks={checks} />
    </div>
  );
}

function ChecksBody({ checks }: { checks: CheckReportReader }) {
  const locale = useChatInterfaceLocale();
  const ns = useNamespaceCopy(appPublishCopy);
  const page = useNamespaceCopy(appPageCopy);
  const [report, setReport] = useState<CheckReport | null>(null);
  const [failed, setFailed] = useState(false);
  const [scanFailed, setScanFailed] = useState(false);
  const [scanning, setScanning] = useState(false);
  const alive = useRef(true);
  const generation = useRef(0);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const load = useCallback(async (run: () => Promise<CheckReport>, scan: boolean) => {
    const mine = ++generation.current;
    if (scan) setScanning(true);
    try {
      const next = await run();
      if (alive.current && mine === generation.current) {
        setReport(next);
        setFailed(false);
        setScanFailed(false);
      }
    } catch (error) {
      if (error instanceof SessionChangedError) return;
      if (alive.current && mine === generation.current) {
        // a failed scan keeps the report that was shown and says so; a failed read has nothing to show
        if (scan) setScanFailed(true);
        else setFailed(true);
      }
    } finally {
      if (alive.current && mine === generation.current) setScanning(false);
    }
  }, []);
  // One operation id per acknowledgment (report + finding): a lost reply retries the SAME id, never a new write.
  const pendingAck = useRef<{ key: string; id: string } | null>(null);
  const acking = useRef(false);
  const ack = checks.acknowledge;
  const acknowledge = useCallback(
    async (input: FindingAcknowledgment) => {
      if (!ack || acking.current) return;
      acking.current = true;
      const key = `${input.reportRef}:${input.findingId}`;
      if (pendingAck.current?.key !== key)
        pendingAck.current = { key, id: globalThis.crypto.randomUUID() };
      const id = pendingAck.current.id;
      const mine = ++generation.current;
      setScanning(true);
      try {
        const next = await ack(input, id);
        pendingAck.current = null;
        if (alive.current && mine === generation.current) {
          setReport(next);
          setScanFailed(false);
        }
      } catch (error) {
        if (error instanceof SessionChangedError || !alive.current || mine !== generation.current)
          return;
        // A refusal (any answered status) means nothing was written: drop the id. A lost reply may have
        // committed: keep the id, show the notice and re-read so the current report decides.
        if (error instanceof CheckReportUnavailable && error.status !== null && error.status < 500)
          pendingAck.current = null;
        if (alive.current && mine === generation.current) setScanFailed(true);
        void load(checks.read, false);
      } finally {
        acking.current = false;
        if (alive.current && mine === generation.current) setScanning(false);
      }
    },
    [ack, load, checks],
  );
  const refresh = useCallback(() => load(checks.read, false), [load, checks]);
  const rescan = checks.scan;
  const scan = useCallback(() => (rescan ? load(rescan, true) : Promise.resolve()), [load, rescan]);
  useEffect(() => {
    void refresh();
    return () => {
      generation.current++;
    };
  }, [refresh]);
  // A failed read is said so, with an ordinary Try again (a plain re-read); the checks never just disappear.
  if (!report)
    return failed ? (
      <ErrorState message={page.portionUnavailable} onRetry={() => void refresh()} />
    ) : (
      <LoadingState />
    );
  const copy = findingsCopyFrom(ns, { formatCopy, formatCount, locale });
  const notice = scanFailed ? (
    <p role="status" className="pc-findings-time">
      {page.portionUnavailable}
    </p>
  ) : null;
  return (
    <>
      <FindingsList
        copy={copy}
        report={report}
        // Check again RERUNS the scan, so it is offered only where a scan can be asked (editors).
        handlers={{
          ...(rescan && !scanning ? { onCheckAgain: () => void scan() } : {}),
          ...(ack && !scanning ? { onPublishAnyway: (input) => void acknowledge(input) } : {}),
        }}
        lastChecked={checkedAt(report.checkedAt, locale)}
      />
      {notice}
    </>
  );
}
